/**
 * Modele du justificatif remis a un tiers pour reclamer sa part.
 *
 * Ce module ne produit **aucun PDF** : il construit une structure de donnees
 * complete et verifiable, que `pdf/document.ts` se contente de mettre en page.
 * Toute l'arithmetique — et donc tout ce qui pourrait etre conteste — est ici,
 * sous test ; le rendu ne calcule rien.
 *
 * Le document doit permettre a son destinataire de **refaire chaque calcul**
 * sans l'application : total paye, parts deduites avant la sienne, assiette,
 * regle appliquee, montant du, deja verse, restant.
 */

import { somme } from '../domain/argent'
import type { Cents, DayKey, Id, Operation, Piece, Reglages, Tiers } from '../domain/types'
import { repartitionDe, type Index } from './donnees'
import { decrireRegle, etatCreance, type StatutCreance } from './repartition'

export interface Deduction {
  tiersNom: string
  regle: string
  montant: Cents
}

export interface LigneJustificatif {
  operation: Operation
  date: DayKey
  libelle: string
  total: Cents
  /** Parts calculees avant celle du destinataire, qui expliquent l'assiette. */
  deductions: Deduction[]
  assiette: Cents
  regle: string
  du: Cents
  recu: Cents
  restant: Cents
  statut: StatutCreance
  /** Ce que l'emetteur supporte lui-meme sur cette facture, par transparence. */
  partEmetteur: Cents
  annexes: number[]
}

export interface ReglementJustificatif {
  date: DayKey
  montant: Cents
  libelleOperation: string
  note: string
}

export interface Annexe {
  numero: number
  pieceId: Id
  nom: string
  operationLibelle: string
  date: DayKey
}

export interface ModeleJustificatif {
  emetteur: { nom: string; adresse: string; iban: string }
  destinataire: { nom: string; adresse: string }
  dateEmission: DayKey
  periode: { du: DayKey; au: DayKey }
  lignes: LigneJustificatif[]
  totalDu: Cents
  totalRecu: Cents
  totalRestant: Cents
  reglements: ReglementJustificatif[]
  annexes: Annexe[]
}

export interface OptionsJustificatif {
  tiersId: Id
  du: DayKey
  au: DayKey
  /** Inclure les factures deja integralement reglees (historique complet)
   *  plutot que les seules sommes encore dues. */
  inclureSoldees: boolean
  dateEmission: DayKey
}

export function construireJustificatif(
  ix: Index,
  pieces: readonly Pick<Piece, 'id' | 'operationId' | 'nom'>[],
  reglages: Pick<Reglages, 'emetteurNom' | 'emetteurAdresse' | 'emetteurIban'>,
  o: OptionsJustificatif,
): ModeleJustificatif {
  const tiers: Tiers | undefined = ix.tiersParId.get(o.tiersId)
  const lignes: LigneJustificatif[] = []
  const reglements: ReglementJustificatif[] = []
  const annexes: Annexe[] = []

  const ops = ix.d.operations
    .filter((op) => op.sens === 'depense' && op.date >= o.du && op.date <= o.au)
    .sort((a, b) => a.date.localeCompare(b.date) || a.libelle.localeCompare(b.libelle))

  for (const op of ops) {
    const r = repartitionDe(ix, op)
    for (const l of r.lignes) {
      if (l.part.tiersId !== o.tiersId) continue
      const creance = etatCreance(l.part, l.montant, ix.reglementsParPart.get(l.part.id) ?? [])
      if (!o.inclureSoldees && creance.statut === 'soldee') continue

      const deductions: Deduction[] =
        l.part.base === 'reste'
          ? r.lignes
              .filter((x) => x.part.ordre < l.part.ordre)
              .map((x) => ({
                tiersNom: ix.tiersParId.get(x.part.tiersId)?.nom ?? '—',
                regle: decrireRegle(x.part),
                montant: x.montant,
              }))
          : []

      const numeros: number[] = []
      for (const p of pieces.filter((p) => p.operationId === op.id)) {
        const deja = annexes.find((a) => a.pieceId === p.id)
        if (deja) {
          numeros.push(deja.numero)
          continue
        }
        const numero = annexes.length + 1
        annexes.push({ numero, pieceId: p.id, nom: p.nom, operationLibelle: op.libelle, date: op.date })
        numeros.push(numero)
      }

      lignes.push({
        operation: op,
        date: op.date,
        libelle: op.libelle,
        total: op.montant,
        deductions,
        assiette: l.assiette,
        regle: decrireRegle(l.part),
        du: l.montant,
        recu: creance.recu,
        restant: creance.restant,
        statut: creance.statut,
        partEmetteur: r.partMoi,
        annexes: numeros,
      })

      for (const reg of creance.reglements) {
        reglements.push({ date: reg.date, montant: reg.montant, libelleOperation: op.libelle, note: reg.note })
      }
    }
  }

  reglements.sort((a, b) => a.date.localeCompare(b.date))
  const totalDu = somme(lignes.map((l) => l.du))
  const totalRecu = somme(lignes.map((l) => l.recu))

  return {
    emetteur: { nom: reglages.emetteurNom, adresse: reglages.emetteurAdresse, iban: reglages.emetteurIban },
    destinataire: { nom: tiers?.nom ?? '', adresse: tiers?.adresse ?? '' },
    dateEmission: o.dateEmission,
    periode: { du: o.du, au: o.au },
    lignes,
    totalDu,
    totalRecu,
    totalRestant: totalDu - totalRecu,
    reglements,
    annexes,
  }
}
